import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiPackage, FiUsers, FiMapPin, FiTrendingUp, FiPlus,
  FiEye, FiDollarSign, FiCalendar, FiArrowRight, FiLink, FiCopy, FiCheck
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { dealerService } from '../../services/dealerService';
import { shedService } from '../../services/shedService';

const DealerDashboard = ({ setActiveTab }) => {
  const { dealerId } = useAuth();
  const [dealer, setDealer] = useState(null);
  const [stats, setStats] = useState({
    totalSheds: 0,
    publishedSheds: 0,
    totalLeads: 0,
    activeCities: 0
  });
  const [recentSheds, setRecentSheds] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [cities, setCities] = useState([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (dealerId) {
      loadDealerInfo();
    }
  }, [dealerId]);

  const loadDealerInfo = async () => {
    try {
      const { data } = await dealerService.getDealerById(dealerId);
      if (data) {
        setDealer(data);
        loadDashboardData(dealerId);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading dealer:', err);
      setLoading(false);
    }
  };

  const loadDashboardData = async (id) => {
    setLoading(true);
    try {
      // Load stats
      const statsRes = await dealerService.getDealerStats(id);
      if (statsRes.data) {
        setStats(statsRes.data);
      }

      // Load dealer's cities
      const citiesRes = await dealerService.getDealerCities(id);
      setCities(citiesRes.data?.map(dc => dc.city) || []);

      // Load dealer's sheds
      const shedsRes = await shedService.getShedsByDealer(id);
      setRecentSheds((shedsRes.data || []).slice(0, 5));

    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {dealer?.company_name || 'Dealer'}
        </h1>
        <p className="text-gray-600">
          Here's what's happening with your shed inventory
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sheds</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSheds}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <FiPackage className="text-primary-600" size={24} />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {stats.publishedSheds} published
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLeads}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FiUsers className="text-green-600" size={24} />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Awaiting follow-up
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Service Areas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeCities}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FiMapPin className="text-blue-600" size={24} />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Cities assigned
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Plan Limit</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalSheds}/{dealer?.max_sheds || 50}
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <FiTrendingUp className="text-amber-600" size={24} />
            </div>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full"
                style={{ width: `${Math.min((stats.totalSheds / (dealer?.max_sheds || 50)) * 100, 100)}%` }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 text-white"
        >
          <h3 className="text-lg font-semibold mb-2">Add New Shed</h3>
          <p className="text-primary-100 text-sm mb-4">
            List a new shed in your inventory
          </p>
          <button 
            onClick={() => setActiveTab && setActiveTab('sheds')}
            className="flex items-center gap-2 bg-white text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-primary-50"
          >
            <FiPlus /> Add Shed
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">View Inventory</h3>
          <p className="text-gray-500 text-sm mb-4">
            Manage your shed listings
          </p>
          <button 
            onClick={() => setActiveTab && setActiveTab('sheds')}
            className="flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700"
          >
            <FiEye /> View All <FiArrowRight />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Leads</h3>
          <p className="text-gray-500 text-sm mb-4">
            Follow up with interested customers
          </p>
          <button 
            onClick={() => setActiveTab && setActiveTab('leads')}
            className="flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700"
          >
            <FiUsers /> View Leads <FiArrowRight />
          </button>
        </motion.div>

        {/* Get My Branded Page */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-6 text-white"
        >
          <h3 className="text-lg font-semibold mb-2">Get My Branded Page</h3>
          <p className="text-emerald-100 text-sm mb-4">
            Share your custom inventory page with customers
          </p>
          {dealer?.slug ? (
            <>
              <div className="bg-white/10 rounded-lg px-3 py-2 mb-3 text-xs font-mono text-emerald-100 truncate">
                {window.location.origin}/dealer/{dealer.slug}
              </div>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/dealer/${dealer.slug}`;
                  navigator.clipboard.writeText(url).then(() => {
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 3000);
                  }).catch(() => {
                    const ta = document.createElement('textarea');
                    ta.value = url;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 3000);
                  });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  linkCopied
                    ? 'bg-emerald-900 text-emerald-200'
                    : 'bg-white text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                {linkCopied ? <><FiCheck /> Link Copied!</> : <><FiCopy /> Copy Link</>}
              </button>
            </>
          ) : (
            <p className="text-emerald-200 text-xs">Contact admin to set up your dealer slug.</p>
          )}
        </motion.div>
      </div>

      {/* Service Areas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Service Areas</h3>
          {cities.length > 0 ? (
            <div className="space-y-3">
              {cities.map(city => (
                <div key={city.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <FiMapPin className="text-blue-600" size={16} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{city.name}</p>
                      <p className="text-sm text-gray-500">{city.state_abbr}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">{city.radius_miles} mi radius</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiMapPin size={32} className="mx-auto mb-2 opacity-50" />
              <p>No service areas assigned yet</p>
              <p className="text-sm">Contact admin to add cities</p>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Sheds</h3>
          {recentSheds.length > 0 ? (
            <div className="space-y-3">
              {recentSheds.map(shed => (
                <div key={shed.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    {shed.images?.[0] ? (
                      <img 
                        src={shed.images[0]} 
                        alt={shed.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FiPackage className="text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{shed.name}</p>
                      <p className="text-sm text-gray-500">{shed.size}</p>
                    </div>
                  </div>
                  <span className="font-medium text-gray-900">{formatPrice(shed.price)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiPackage size={32} className="mx-auto mb-2 opacity-50" />
              <p>No sheds listed yet</p>
              <button className="text-primary-600 font-medium mt-2">Add your first shed</button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Subscription Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="mt-6 bg-gray-50 rounded-xl p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
            <FiCalendar className="text-gray-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Your Plan</p>
            <p className="font-medium text-gray-900 capitalize">
              {dealer?.subscription_plan || 'Basic'} Plan
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          dealer?.subscription_status === 'active' 
            ? 'bg-green-100 text-green-800'
            : 'bg-amber-100 text-amber-800'
        }`}>
          {dealer?.subscription_status || 'Trial'}
        </span>
      </motion.div>
    </div>
  );
};

export default DealerDashboard;
